import {
  applyBooleanField,
  applyNumberArrayField,
  applyNumberField,
  applyRecordField,
  applyRouteFromShare,
  applySortConfigField,
  applyStringArrayField,
  applyStringField,
  validateSharePage,
} from './shareApplyUtils';
import {
  setSelectedEvents,
  setHasSelection,
  setSelection,
  type SelectionData,
} from '../store/selectionStore';
import {
  setSelectedGradesAggregates,
  setSelectedLegsAggregates,
  setSelectedRacesAggregates,
  setSelectedSources as filterStoreSetSelectedSources,
  setSelectedStatesAggregates,
  setManeuverTrainingRacing,
  setManeuverTackEntry,
  setSelectedGradesManeuvers,
  setSelectedStatesManeuvers,
  setSelectedRacesManeuvers,
  setSelectedLegsManeuvers,
  setStartDate,
  setEndDate,
} from '../store/filterStore';
import {
  setColor,
  setEventType,
  setPhase,
  setTws,
  setGrouped,
  setGroupDisplayMode,
} from '../store/globalStore';
import { maneuverBaselineStore } from '../store/maneuverBaselineStore';
import { holdManeuverStateRestoreForShare } from './maneuverStateReconcile';

export const PERFORMANCE_SHARE_PAGE_KEYS = new Set([
  'FleetPerformance',
  'Performance',
  'FleetPerformanceHistory',
  'PerformanceHistory',
]);

export const MANEUVERS_SHARE_PAGE_KEYS = new Set([
  'FleetManeuvers',
  'Maneuvers',
  'FleetManeuversHistory',
  'ManeuversHistory',
]);

export interface CheatSheetShareState {
  groupBy: string;
  twsIndex: number;
  selectedMetricKey: string;
  legType: string;
  sortConfig: { key: string; direction: 'asc' | 'desc' };
  columnScales: Record<string, string>;
  formattedColumns: string[];
  showDeltas: boolean;
  groupByManeuver: string;
  selectedMetricKeyManeuver: string;
  sortConfigManeuver: { key: string; direction: 'asc' | 'desc' };
  columnScalesManeuver: Record<string, string>;
  formattedColumnsManeuver: string[];
  showDeltasManeuver: boolean;
  sortConfigAccelerations: { key: string; direction: 'asc' | 'desc' };
  columnScalesAccelerations: Record<string, string>;
  formattedColumnsAccelerations: string[];
  showDeltasAccelerations: boolean;
  groupByAccelerations: string;
  selectedMetricKeyAccelerations: string;
  selectedConfiguration: string;
  filterYear?: string;
  filterEvent?: string;
  filterState?: string;
  selectedSources?: string[];
}

export function buildCheatSheetShareState(state: CheatSheetShareState): Record<string, unknown> {
  return { ...state };
}

export function applyCheatSheetShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'groupBy', setters.setGroupBy as (v: string) => void);
  applyNumberField(obj, 'twsIndex', setters.setTwsIndex as (v: number) => void);
  applyStringField(obj, 'selectedMetricKey', setters.setSelectedMetricKey as (v: string) => void);
  applyStringField(obj, 'legType', setters.setLegType as (v: string) => void);
  applySortConfigField(obj, 'sortConfig', setters.setSortConfig as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScales', setters.setColumnScales as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumns', setters.setFormattedColumns as (v: string[]) => void);
  applyBooleanField(obj, 'showDeltas', setters.setShowDeltas as (v: boolean) => void);
  applyStringField(obj, 'groupByManeuver', setters.setGroupByManeuver as (v: string) => void);
  applyStringField(obj, 'selectedMetricKeyManeuver', setters.setSelectedMetricKeyManeuver as (v: string) => void);
  applySortConfigField(obj, 'sortConfigManeuver', setters.setSortConfigManeuver as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScalesManeuver', setters.setColumnScalesManeuver as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumnsManeuver', setters.setFormattedColumnsManeuver as (v: string[]) => void);
  applyBooleanField(obj, 'showDeltasManeuver', setters.setShowDeltasManeuver as (v: boolean) => void);
  applySortConfigField(obj, 'sortConfigAccelerations', setters.setSortConfigAccelerations as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScalesAccelerations', setters.setColumnScalesAccelerations as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumnsAccelerations', setters.setFormattedColumnsAccelerations as (v: string[]) => void);
  applyBooleanField(obj, 'showDeltasAccelerations', setters.setShowDeltasAccelerations as (v: boolean) => void);
  applyStringField(obj, 'groupByAccelerations', setters.setGroupByAccelerations as (v: string) => void);
  applyStringField(obj, 'selectedMetricKeyAccelerations', setters.setSelectedMetricKeyAccelerations as (v: string) => void);
  applyStringField(obj, 'selectedConfiguration', setters.setSelectedConfiguration as (v: string) => void);
  applyStringField(obj, 'filterYear', setters.setFilterYear as (v: string) => void);
  applyStringField(obj, 'filterEvent', setters.setFilterEvent as (v: string) => void);
  // State defaults to H0 when the share link predates the state filter (old links → H0).
  applyStringField(obj, 'filterState', setters.setFilterState as (v: string) => void);
  if (Array.isArray(obj.selectedSources)) {
    filterStoreSetSelectedSources((obj.selectedSources as unknown[]).map((x) => String(x)));
  }
  return true;
}

export interface SummaryShareState {
  selectedRace: string;
  legType: string;
  dataMode: string;
  showDeltasResults: boolean;
  showDeltasAverages: boolean;
  sortConfigResults: { key: string; direction: 'asc' | 'desc' };
  sortConfigSummary: { key: string; direction: 'asc' | 'desc' };
  sortConfigAvg: { key: string; direction: 'asc' | 'desc' };
  columnScalesResults: Record<string, string>;
  columnScalesSummary: Record<string, string>;
  columnScalesAvg: Record<string, string>;
  formattedColumnsResults: string[];
  formattedColumnsSummary: string[];
  formattedColumnsAvg: string[];
  isTrainingHourMode?: boolean;
}

export function buildSummaryShareState(state: SummaryShareState): Record<string, unknown> {
  return { ...state };
}

export function applySummaryShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'selectedRace', setters.setSelectedRace as (v: string) => void);
  applyStringField(obj, 'legType', setters.setLegType as (v: string) => void);
  applyStringField(obj, 'dataMode', setters.setDataMode as (v: string) => void);
  applyBooleanField(obj, 'showDeltasResults', setters.setShowDeltasResults as (v: boolean) => void);
  applyBooleanField(obj, 'showDeltasAverages', setters.setShowDeltasAverages as (v: boolean) => void);
  applySortConfigField(obj, 'sortConfigResults', setters.setSortConfigResults as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applySortConfigField(obj, 'sortConfigSummary', setters.setSortConfigSummary as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applySortConfigField(obj, 'sortConfigAvg', setters.setSortConfigAvg as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScalesResults', setters.setColumnScalesResults as (v: Record<string, string>) => void);
  applyRecordField(obj, 'columnScalesSummary', setters.setColumnScalesSummary as (v: Record<string, string>) => void);
  applyRecordField(obj, 'columnScalesAvg', setters.setColumnScalesAvg as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumnsResults', setters.setFormattedColumnsResults as (v: string[]) => void);
  applyStringArrayField(obj, 'formattedColumnsSummary', setters.setFormattedColumnsSummary as (v: string[]) => void);
  applyStringArrayField(obj, 'formattedColumnsAvg', setters.setFormattedColumnsAvg as (v: string[]) => void);
  if (setters.setIsTrainingHourMode) {
    applyBooleanField(obj, 'isTrainingHourMode', setters.setIsTrainingHourMode as (v: boolean) => void);
  }
  return true;
}

export interface PrestartShareState {
  selectedRace: string;
  view: string;
  selectedTimeSec: number | null;
  selectedSourceNames: string[];
  sortConfig: { key: string; direction: 'asc' | 'desc' };
  columnScales: Record<string, string>;
  formattedColumns: string[];
}

export function buildPrestartShareState(state: PrestartShareState): Record<string, unknown> {
  return { ...state };
}

export function applyPrestartShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'selectedRace', setters.setSelectedRace as (v: string) => void);
  applyStringField(obj, 'view', setters.setView as (v: string) => void);
  if (obj.selectedTimeSec === null || typeof obj.selectedTimeSec === 'number') {
    (setters.setSelectedTimeSec as (v: number | null) => void)(obj.selectedTimeSec as number | null);
  }
  applyStringArrayField(obj, 'selectedSourceNames', setters.setSelectedSourceNames as (v: string[]) => void);
  applySortConfigField(obj, 'sortConfig', setters.setSortConfig as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScales', setters.setColumnScales as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumns', setters.setFormattedColumns as (v: string[]) => void);
  if (Array.isArray(obj.selectedSourceNames)) {
    filterStoreSetSelectedSources((obj.selectedSourceNames as unknown[]).map((x) => String(x)));
  }
  return true;
}

/** Dataset PRESTARTS share: start-time selection stays on this page and does not rewrite the fleet source filter. */
export function applyDatasetPrestartsShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'view', setters.setView as (v: string) => void);
  if (obj.selectedTimeSec === null || typeof obj.selectedTimeSec === 'number') {
    (setters.setSelectedTimeSec as (v: number | null) => void)(obj.selectedTimeSec as number | null);
  }
  applyStringArrayField(obj, 'selectedSourceNames', setters.setSelectedSourceNames as (v: string[]) => void);
  applySortConfigField(obj, 'sortConfig', setters.setSortConfig as (v: { key: string; direction: 'asc' | 'desc' }) => void);
  applyRecordField(obj, 'columnScales', setters.setColumnScales as (v: Record<string, string>) => void);
  applyStringArrayField(obj, 'formattedColumns', setters.setFormattedColumns as (v: string[]) => void);
  return true;
}

export interface PerformanceDriversShareState {
  configuration: string;
  pointOfSail: string;
  twsSelection?: string | number;
}

export function buildPerformanceDriversShareState(state: PerformanceDriversShareState): Record<string, unknown> {
  return { ...state };
}

export function applyPerformanceDriversShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'configuration', setters.setConfiguration as (v: string) => void);
  applyStringField(obj, 'pointOfSail', setters.setPointOfSail as (v: string) => void);
  if (setters.setTwsSelection) {
    const rawTws = obj.twsSelection;
    if (rawTws === 'ALL' || rawTws === 'all') {
      (setters.setTwsSelection as (v: string | number) => void)('ALL');
    } else if (typeof rawTws === 'number' && Number.isFinite(rawTws)) {
      (setters.setTwsSelection as (v: string | number) => void)(rawTws);
    } else if (typeof rawTws === 'string' && rawTws.trim() !== '') {
      const parsed = Number(rawTws);
      if (Number.isFinite(parsed)) {
        (setters.setTwsSelection as (v: string | number) => void)(parsed);
      }
    }
  }
  return true;
}

export interface CalibrationShareState {
  uwDw: string;
  foilingDisp: string;
  selectedSources?: string[];
}

export function buildCalibrationShareState(state: CalibrationShareState): Record<string, unknown> {
  return { ...state };
}

export function applyCalibrationShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'uwDw', setters.setUwDw as (v: string) => void);
  applyStringField(obj, 'foilingDisp', setters.setFoilingDisp as (v: string) => void);
  if (Array.isArray(obj.selectedSources)) {
    filterStoreSetSelectedSources((obj.selectedSources as unknown[]).map((x) => String(x)));
  }
  return true;
}

export interface ManeuversShareState {
  view: string;
  showTimeline: boolean;
  eventType: string;
  phase: string;
  color: string;
  tws: string;
  grouped: string;
  groupDisplayMode: string;
  selectedRaces: (number | string)[];
  selectedLegs: number[];
  selectedGrades: number[];
  selectedStates: string[];
  selectedSourceIds?: number[];
  maneuverTrainingRacing?: string | null;
  maneuverTackEntry?: 'PORT' | 'STBD' | null;
  filterYear?: string;
  filterEvent?: string;
  filterConfigValue?: string;
  filterState?: string;
  startDate?: string;
  endDate?: string;
  selectedEvents?: number[];
  hasSelection?: boolean;
  maneuverSelection?: SelectionData[];
  /** Live Maneuvers display scope in minutes (0 = All). Absent on non-live maneuver pages. */
  liveTimeWindowMinutes?: number;
  /** null is an explicit "No baseline". Absent on links saved before baselines were shared. */
  baselineId?: number | null;
  baselineName?: string;
  baselineConfig?: string;
  baselineColor?: string;
}

/**
 * Page keys whose latest applied share named a baseline (including an explicit "No baseline").
 *
 * Links saved before baselines were shared leave the key unset, so the page still restores the
 * viewer's own selection. Consumed once, after `shareRestoreReady` resolves.
 */
const shareSpecifiedBaselinePages = new Set<string>();

export function markShareBaselineSpecified(pageKey: string, specified: boolean): void {
  if (specified) shareSpecifiedBaselinePages.add(pageKey);
  else shareSpecifiedBaselinePages.delete(pageKey);
}

export function consumeShareSpecifiedBaseline(pageKey: string): boolean {
  const specified = shareSpecifiedBaselinePages.has(pageKey);
  shareSpecifiedBaselinePages.delete(pageKey);
  return specified;
}

/** The baseline on screen, for the share mirror. Reads the store so the mirror tracks a change. */
export function currentManeuverBaselineShare(): Pick<
  ManeuversShareState,
  'baselineId' | 'baselineName' | 'baselineConfig' | 'baselineColor'
> {
  return {
    baselineId: maneuverBaselineStore.baselineId(),
    baselineName: maneuverBaselineStore.baselineName(),
    baselineConfig: maneuverBaselineStore.baselineConfig(),
    baselineColor: maneuverBaselineStore.baselineColor(),
  };
}

export function buildManeuversShareState(state: ManeuversShareState): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...state };
  if (Array.isArray(state.maneuverSelection) && state.maneuverSelection.length > 0) {
    payload.maneuverSelection = JSON.parse(JSON.stringify(state.maneuverSelection));
  }
  return payload;
}

export function applyManeuversShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'view', setters.setView as (v: string) => void);
  applyBooleanField(obj, 'showTimeline', setters.setShowTimeline as (v: boolean) => void);
  // Guarded on the setter: only the live maneuvers page supplies one, and applyNumberField would
  // call an undefined setter if a live share link were applied on a page that doesn't have it.
  if (typeof setters.setLiveTimeWindowMinutes === 'function') {
    applyNumberField(obj, 'liveTimeWindowMinutes', setters.setLiveTimeWindowMinutes as (v: number) => void);
  }
  if (typeof obj.eventType === 'string') {
    // Before the type changes, so the per-type state restore does not load the viewer's own chips
    // over the ones this link carries.
    if (Array.isArray(obj.selectedStates)) {
      holdManeuverStateRestoreForShare(obj.eventType);
    }
    setEventType(obj.eventType);
  }
  if (typeof obj.phase === 'string') setPhase(obj.phase);
  if (typeof obj.color === 'string') setColor(obj.color);
  if (typeof obj.tws === 'string') setTws(obj.tws);
  if (typeof obj.grouped === 'string') setGrouped(obj.grouped);
  if (typeof obj.groupDisplayMode === 'string') setGroupDisplayMode(obj.groupDisplayMode);
  if (Array.isArray(obj.selectedRaces)) {
    (setters.setSelectedRaces as (v: (number | string)[]) => void)(obj.selectedRaces as (number | string)[]);
    setSelectedRacesManeuvers((obj.selectedRaces as unknown[]).map((x) => String(x)));
  }
  if (Array.isArray(obj.selectedLegs)) {
    applyNumberArrayField(obj, 'selectedLegs', setters.setSelectedLegs as (v: number[]) => void);
    setSelectedLegsManeuvers((obj.selectedLegs as number[]).map(String));
  }
  if (Array.isArray(obj.selectedGrades)) {
    applyNumberArrayField(obj, 'selectedGrades', setters.setSelectedGrades as (v: number[]) => void);
    setSelectedGradesManeuvers((obj.selectedGrades as number[]).map(String));
  }
  if (Array.isArray(obj.selectedStates)) {
    applyStringArrayField(obj, 'selectedStates', setters.setSelectedStates as (v: string[]) => void);
    setSelectedStatesManeuvers(obj.selectedStates as string[]);
  }
  if (Array.isArray(obj.selectedSourceIds) && setters.setSelectedSourceIds) {
    (setters.setSelectedSourceIds as (v: Set<number>) => void)(new Set(obj.selectedSourceIds as number[]));
  }
  if (obj.maneuverTrainingRacing === 'TRAINING' || obj.maneuverTrainingRacing === 'RACING' || obj.maneuverTrainingRacing === null) {
    setManeuverTrainingRacing(obj.maneuverTrainingRacing);
  }
  if (obj.maneuverTackEntry === 'PORT' || obj.maneuverTackEntry === 'STBD' || obj.maneuverTackEntry === null) {
    setManeuverTackEntry(obj.maneuverTackEntry);
  }
  applyStringField(obj, 'filterYear', setters.setFilterYear as (v: string) => void);
  applyStringField(obj, 'filterEvent', setters.setFilterEvent as (v: string) => void);
  applyStringField(obj, 'filterConfigValue', setters.setFilterConfigValue as (v: string) => void);
  applyStringField(obj, 'filterState', setters.setFilterState as (v: string) => void);
  applyStringField(obj, 'startDate', setters.setStartDate as (v: string) => void);
  applyStringField(obj, 'endDate', setters.setEndDate as (v: string) => void);
  if (typeof obj.startDate === 'string') setStartDate(obj.startDate);
  if (typeof obj.endDate === 'string') setEndDate(obj.endDate);
  if (Array.isArray(obj.selectedEvents)) {
    const ev = (obj.selectedEvents as unknown[]).filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
    setSelectedEvents(ev);
  }
  const ms = obj.maneuverSelection;
  if (Array.isArray(ms)) {
    const rows = ms.filter((x): x is SelectionData => x != null && typeof x === 'object' && !Array.isArray(x));
    setSelection(rows);
  }
  const inferredHas =
    typeof obj.hasSelection === 'boolean'
      ? obj.hasSelection
      : (Array.isArray(obj.selectedEvents) && obj.selectedEvents.length > 0) ||
        (Array.isArray(ms) && ms.length > 0);
  setHasSelection(Boolean(inferredHas));
  markShareBaselineSpecified(pageKey, 'baselineId' in obj);
  if ('baselineId' in obj && typeof setters.applySharedBaseline === 'function') {
    const rawId = obj.baselineId;
    const id = typeof rawId === 'number' && Number.isInteger(rawId) && rawId > 0 ? rawId : null;
    setters.applySharedBaseline({
      baselineId: id,
      baselineName: typeof obj.baselineName === 'string' ? obj.baselineName : '',
      baselineConfig: typeof obj.baselineConfig === 'string' ? obj.baselineConfig : '',
      baselineColor: typeof obj.baselineColor === 'string' ? obj.baselineColor : undefined,
    });
  }
  return true;
}

export interface PerformanceShareState {
  plotType?: string;
  UwDw?: string;
  showUwDw?: boolean;
  showTimeline?: boolean;
  color?: string;
  twsBin?: string;
  importanceSort?: string;
  selectedTargetName?: string;
  selectedTargetColor?: string;
  selectedTrainingRacing?: 'TRAINING' | 'RACING' | null;
  /** PORT/STBD tack filter (fleet performance pages). */
  selectedTack?: 'PORT' | 'STBD' | null;
  xAxis?: string;
  cloudType?: string;
  zoom?: unknown;
  filterGrades?: string;
  filterYear?: string;
  filterEvent?: string;
  filterConfig?: string;
  filterState?: string;
  selectedRacesAggregates?: string[];
  selectedLegsAggregates?: string[];
  selectedGradesAggregates?: string[];
  selectedStatesAggregates?: string[];
  selectedSources?: string[];
  legendSelectedSource?: string | null;
  selectedEvents?: number[];
  hasSelection?: boolean;
  performanceSelection?: SelectionData[];
  localTrainingRacingFilter?: string | null;
}

export function buildPerformanceShareState(state: PerformanceShareState): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...state };
  if (Array.isArray(state.performanceSelection) && state.performanceSelection.length > 0) {
    payload.performanceSelection = JSON.parse(JSON.stringify(state.performanceSelection));
  }
  return payload;
}

export function applyPerformanceShareState(
  obj: Record<string, unknown>,
  pageKey: string,
  setters: Record<string, (value: unknown) => void>
): boolean {
  if (!PERFORMANCE_SHARE_PAGE_KEYS.has(pageKey) || !validateSharePage(obj, pageKey)) {
    return false;
  }
  applyRouteFromShare(obj);
  applyStringField(obj, 'plotType', setters.setPlotType as (v: string) => void);
  applyStringField(obj, 'UwDw', setters.setUwDw as (v: string) => void);
  applyBooleanField(obj, 'showUwDw', setters.setShowUwDw as (v: boolean) => void);
  applyBooleanField(obj, 'showTimeline', setters.setShowTimeline as (v: boolean) => void);
  applyStringField(obj, 'color', setters.setColor as (v: string) => void);
  applyStringField(obj, 'twsBin', setters.setTwsBin as (v: string) => void);
  applyStringField(obj, 'importanceSort', setters.setImportanceSort as (v: string) => void);
  applyStringField(obj, 'selectedTargetName', setters.setSelectedTargetName as (v: string) => void);
  if (setters.setSelectedTargetColor) {
    applyStringField(obj, 'selectedTargetColor', setters.setSelectedTargetColor as (v: string) => void);
  }
  if (obj.selectedTrainingRacing === 'TRAINING' || obj.selectedTrainingRacing === 'RACING' || obj.selectedTrainingRacing === null) {
    (setters.setSelectedTrainingRacing as (v: 'TRAINING' | 'RACING' | null) => void)(obj.selectedTrainingRacing);
  }
  if (obj.selectedTack === 'PORT' || obj.selectedTack === 'STBD' || obj.selectedTack === null) {
    (setters.setSelectedTack as (v: 'PORT' | 'STBD' | null) => void)?.(obj.selectedTack);
  }
  applyStringField(obj, 'xAxis', setters.setXAxis as (v: string) => void);
  applyStringField(obj, 'cloudType', setters.setCloudType as (v: string) => void);
  if (setters.setZoom && obj.zoom !== undefined) {
    setters.setZoom(obj.zoom);
  }
  applyStringField(obj, 'filterGrades', setters.setFilterGrades as (v: string) => void);
  applyStringField(obj, 'filterYear', setters.setFilterYear as (v: string) => void);
  applyStringField(obj, 'filterEvent', setters.setFilterEvent as (v: string) => void);
  applyStringField(obj, 'filterConfig', setters.setFilterConfig as (v: string) => void);
  applyStringField(obj, 'filterState', setters.setFilterState as (v: string) => void);
  if (setters.setSelectedRacesAggregates) {
    applyStringArrayField(obj, 'selectedRacesAggregates', setters.setSelectedRacesAggregates as (v: string[]) => void);
  }
  if (setters.setSelectedLegsAggregates) {
    applyStringArrayField(obj, 'selectedLegsAggregates', setters.setSelectedLegsAggregates as (v: string[]) => void);
  }
  if (setters.setSelectedGradesAggregates) {
    applyStringArrayField(obj, 'selectedGradesAggregates', setters.setSelectedGradesAggregates as (v: string[]) => void);
  }
  if (setters.setSelectedStatesAggregates) {
    applyStringArrayField(obj, 'selectedStatesAggregates', setters.setSelectedStatesAggregates as (v: string[]) => void);
  }
  if (Array.isArray(obj.selectedRacesAggregates)) setSelectedRacesAggregates((obj.selectedRacesAggregates as unknown[]).map(String));
  if (Array.isArray(obj.selectedLegsAggregates)) setSelectedLegsAggregates((obj.selectedLegsAggregates as unknown[]).map(String));
  if (Array.isArray(obj.selectedGradesAggregates)) setSelectedGradesAggregates((obj.selectedGradesAggregates as unknown[]).map(String));
  if (Array.isArray(obj.selectedStatesAggregates)) setSelectedStatesAggregates(obj.selectedStatesAggregates as string[]);
  if (Array.isArray(obj.selectedSources)) {
    filterStoreSetSelectedSources((obj.selectedSources as unknown[]).map((x) => String(x)));
  }
  if (obj.legendSelectedSource === null || typeof obj.legendSelectedSource === 'string') {
    (setters.setLegendSelectedSource as (v: string | null) => void)(obj.legendSelectedSource as string | null);
  }
  if (obj.localTrainingRacingFilter === 'TRAINING' || obj.localTrainingRacingFilter === 'RACING' || obj.localTrainingRacingFilter === null) {
    (setters.setLocalTrainingRacingFilter as (v: 'TRAINING' | 'RACING' | null) => void)?.(obj.localTrainingRacingFilter);
  }
  if (Array.isArray(obj.selectedEvents)) {
    const ev = (obj.selectedEvents as unknown[]).filter((x): x is number => typeof x === 'number' && Number.isFinite(x));
    setSelectedEvents(ev);
  }
  const ps = obj.performanceSelection;
  if (Array.isArray(ps)) {
    const rows = ps.filter((x): x is SelectionData => x != null && typeof x === 'object' && !Array.isArray(x));
    setSelection(rows.length > 0 ? rows : []);
  }
  const inferredHas =
    typeof obj.hasSelection === 'boolean'
      ? obj.hasSelection
      : (Array.isArray(obj.selectedEvents) && obj.selectedEvents.length > 0) ||
        (Array.isArray(ps) && ps.length > 0);
  setHasSelection(Boolean(inferredHas));
  applyStringField(obj, 'startDate', setters.setStartDate as (v: string) => void);
  applyStringField(obj, 'endDate', setters.setEndDate as (v: string) => void);
  if (typeof obj.startDate === 'string') setStartDate(obj.startDate);
  if (typeof obj.endDate === 'string') setEndDate(obj.endDate);
  return true;
}
